/**
 * PaymentModal — Payment options for a cobranca
 * Shows card, boleto, and PIX payment methods
 * Integrates with Stripe API and PIX QR code generation
 */
import { useState } from "react";
import PropTypes from "prop-types";
import { useModalA11y } from "../../shared/hooks.js";
import { createPayment } from "../../services/paymentService.js";
import { logWarn } from "../../shared/logger.js";
import { RADIUS } from "../../shared/theme.js";

export default function PaymentModal({ cobranca, onClose, onPaymentSuccess, setToast, T }) {
  const isMobile = typeof window !== "undefined" ? window.innerWidth < 768 : false;
  const trapRef = useModalA11y(onClose);

  const [loading, setLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [paymentData, setPaymentData] = useState(null);

  const valor =
    cobranca.valor > 0 ? cobranca.valor : cobranca.patrimonio * (cobranca.feeAnual / 100 / 12);

  // ── Handle payment method selection ────────────────────────────
  const handleStartPayment = async (method) => {
    setLoading(true);
    try {
      const result = await createPayment(cobranca.id, method);

      if (result.error) {
        setToast?.(`Erro ao iniciar pagamento: ${result.error}`, "error");
        logWarn("PaymentModal", "createPayment error", { error: result.error });
        setLoading(false);
        return;
      }

      setPaymentData(result);
      setSelectedMethod(method);

      // If Stripe, open payment URL in new window
      if (result.payment_url) {
        const paymentWindow = window.open(
          result.payment_url,
          "stripe_checkout",
          "width=600,height=700"
        );
        if (!paymentWindow) {
          setToast?.("Pop-up bloqueado. Clique no link para pagar.", "warning");
        }
      }

      setToast?.("Pagamento iniciado!", "success");
    } catch (err) {
      logWarn("PaymentModal", "handleStartPayment exception", { error: err?.message });
      setToast?.("Erro ao processar pagamento", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Copy PIX copia-e-cola to clipboard ─────────────────────────
  const copyPixCode = async () => {
    if (paymentData?.pix_copia_cola) {
      try {
        await navigator.clipboard.writeText(paymentData.pix_copia_cola);
        setToast?.("Código PIX copiado!", "success");
      } catch {
        setToast?.("Erro ao copiar", "error");
      }
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        padding: 16,
      }}
    >
      {/* Overlay */}
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(2px)",
        }}
      />

      {/* Modal */}
      <div
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-title"
        style={{
          position: "relative",
          background: T.cardBg,
          borderRadius: RADIUS.lg,
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          maxWidth: isMobile ? "95vw" : 500,
          width: "100%",
          maxHeight: "90vh",
          overflow: "auto",
          padding: isMobile ? 20 : 28,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 20,
          }}
        >
          <div>
            <h2 id="pm-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.text }}>
              💳 Formas de Pagamento
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: T.sub }}>
              {cobranca.clientName} • {cobranca.refMes}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              cursor: "pointer",
              color: T.sub,
              padding: 4,
            }}
          >
            ✕
          </button>
        </div>

        {/* Valor da Cobrança */}
        <div
          style={{
            background: T.infoBg,
            borderRadius: RADIUS.md,
            padding: "12px 14px",
            marginBottom: 20,
            border: `1px solid ${T.info}30`,
          }}
        >
          <p
            style={{
              margin: "0 0 4px",
              fontSize: 10,
              color: T.muted,
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            Valor a Pagar
          </p>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 800, color: T.text }}>
            R${" "}
            {valor.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>

        {/* Payment Options */}
        {!paymentData ? (
          <div
            style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}
          >
            {/* Cartão de Crédito */}
            <button
              onClick={() => handleStartPayment("stripe_card")}
              disabled={loading}
              style={{
                padding: 14,
                borderRadius: RADIUS.md,
                border: `2px solid ${selectedMethod === "stripe_card" ? T.accent : T.border}`,
                background: selectedMethod === "stripe_card" ? T.accentBg || T.accent + "15" : T.bg,
                cursor: loading ? "wait" : "pointer",
                transition: "all 0.2s ease",
                opacity: loading ? 0.7 : 1,
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 6 }}>💳</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 2 }}>
                Cartão
              </div>
              <div style={{ fontSize: 10, color: T.sub }}>Débito ou Crédito</div>
            </button>

            {/* Boleto */}
            <button
              onClick={() => handleStartPayment("stripe_boleto")}
              disabled={loading}
              style={{
                padding: 14,
                borderRadius: RADIUS.md,
                border: `2px solid ${selectedMethod === "stripe_boleto" ? T.accent : T.border}`,
                background:
                  selectedMethod === "stripe_boleto" ? T.accentBg || T.accent + "15" : T.bg,
                cursor: loading ? "wait" : "pointer",
                transition: "all 0.2s ease",
                opacity: loading ? 0.7 : 1,
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 6 }}>📋</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 2 }}>
                Boleto
              </div>
              <div style={{ fontSize: 10, color: T.sub }}>Vencimento configurável</div>
            </button>

            {/* PIX */}
            <button
              onClick={() => handleStartPayment("pix")}
              disabled={loading}
              style={{
                padding: 14,
                borderRadius: RADIUS.md,
                border: `2px solid ${selectedMethod === "pix" ? T.accent : T.border}`,
                background: selectedMethod === "pix" ? T.accentBg || T.accent + "15" : T.bg,
                cursor: loading ? "wait" : "pointer",
                transition: "all 0.2s ease",
                opacity: loading ? 0.7 : 1,
                gridColumn: isMobile ? "1" : "1/3",
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 6 }}>📱</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, marginBottom: 2 }}>
                PIX
              </div>
              <div style={{ fontSize: 10, color: T.sub }}>Transferência instantânea</div>
            </button>
          </div>
        ) : (
          // ── Payment Details / Success State ────────────────────────
          <div style={{ marginTop: 20 }}>
            {selectedMethod === "pix" && paymentData.pix_qr_code && (
              <div style={{ textAlign: "center" }}>
                <p style={{ fontSize: 12, color: T.sub, marginBottom: 12 }}>
                  Escaneie o código QR ou copie o código PIX abaixo
                </p>

                {/* QR Code Placeholder */}
                <div
                  style={{
                    background: "#fff",
                    borderRadius: RADIUS.md,
                    padding: 16,
                    marginBottom: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 200,
                    border: `1px solid ${T.border}`,
                  }}
                >
                  <canvas id="pix-qr-canvas" style={{ maxWidth: "100%", height: "auto" }} />
                </div>

                {/* Copia e Cola */}
                {paymentData.pix_copia_cola && (
                  <div
                    style={{
                      background: T.bg,
                      borderRadius: RADIUS.md,
                      padding: 12,
                      marginBottom: 12,
                      border: `1px dashed ${T.border}`,
                      wordBreak: "break-all",
                    }}
                  >
                    <p style={{ margin: "0 0 8px", fontSize: 10, color: T.muted, fontWeight: 600 }}>
                      CÓDIGO PIX (copia e cola)
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 11,
                        fontFamily: "monospace",
                        color: T.text,
                        wordWrap: "break-word",
                      }}
                    >
                      {paymentData.pix_copia_cola}
                    </p>
                  </div>
                )}

                <button
                  onClick={copyPixCode}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: RADIUS.sm,
                    border: "none",
                    background: T.accent || "#7C3AED",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: "pointer",
                    marginBottom: 12,
                  }}
                >
                  📋 Copiar Código PIX
                </button>
              </div>
            )}

            {(selectedMethod === "stripe_card" || selectedMethod === "stripe_boleto") &&
              paymentData.payment_url && (
                <div style={{ textAlign: "center" }}>
                  <p style={{ fontSize: 12, color: T.sub, marginBottom: 12 }}>
                    Clique no botão abaixo para abrir a página de pagamento Stripe
                  </p>
                  <a
                    href={paymentData.payment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-block",
                      padding: "10px 20px",
                      borderRadius: RADIUS.sm,
                      background: "#005EB8",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 12,
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    🔗 Abrir Stripe Checkout
                  </a>
                </div>
              )}
          </div>
        )}

        {/* Footer */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 20,
            borderTop: `1px solid ${T.border}`,
            paddingTop: 16,
          }}
        >
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: "10px 14px",
              borderRadius: RADIUS.sm,
              border: `1px solid ${T.border}`,
              background: T.bg,
              color: T.text,
              fontWeight: 700,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Fechar
          </button>
          {paymentData && (
            <button
              onClick={() => {
                onPaymentSuccess?.();
                onClose();
              }}
              style={{
                flex: 1,
                padding: "10px 14px",
                borderRadius: RADIUS.sm,
                border: "none",
                background: "#16A34A",
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              ✓ Confirmar Pagamento
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

PaymentModal.propTypes = {
  cobranca: PropTypes.shape({
    id: PropTypes.string.isRequired,
    clientName: PropTypes.string.isRequired,
    refMes: PropTypes.string.isRequired,
    patrimonio: PropTypes.number.isRequired,
    feeAnual: PropTypes.number.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onPaymentSuccess: PropTypes.func,
  setToast: PropTypes.func,
  T: PropTypes.object,
};
